const siteKey = process.env.REACT_APP_RECAPTCHA;
const errorMessage = 'Verification could not be completed. Please try again.';
let loading;

// Log diagnostic context only. Tokens, keys, request bodies, and raw errors may contain private data.
const logRecaptchaError = (action, phase, reason, startedAt, details = {}) => {
    // eslint-disable-next-line no-console
    console.error('reCAPTCHA error', {
        action,
        phase,
        reason,
        durationMs: Date.now() - startedAt,
        // Limit route depth so paths such as /login/reset/:userId/:resetToken stay private.
        page: window.location.pathname.split('/').slice(0, 3).join('/'),
        online: navigator.onLine,
        userAgent: navigator.userAgent,
        visibility: document.visibilityState,
        siteKeySuffix: siteKey ? siteKey.slice(-6) : undefined,
        ...details,
    });
};

const safeCode = value => (typeof value === 'string' && /^[A-Za-z][A-Za-z0-9_]{0,79}$/.test(value) ? value : undefined);

const logRecaptchaStart = (action) => {
    // A marker on every attempt distinguishes a stale browser bundle from a missing error path.
    // eslint-disable-next-line no-console
    console.info('reCAPTCHA attempt', {
        action,
        page: window.location.pathname.split('/').slice(0, 3).join('/'),
        siteKeySuffix: siteKey ? siteKey.slice(-6) : undefined,
    });
};

const loadEnterprise = (action) => {
    if (!siteKey) {
        logRecaptchaError(action, 'configuration', 'missing_site_key', Date.now());
        return Promise.reject(new Error(errorMessage));
    }
    if (loading) return loading;

    loading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const startedAt = Date.now();
        let timer;
        let settled = false;
        const fail = (reason) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            script.remove();
            loading = null;
            logRecaptchaError(action, 'script', reason, startedAt);
            reject(new Error(errorMessage));
        };
        timer = setTimeout(() => fail('timeout'), 15000);
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.onerror = () => fail('load_error');
        script.onload = () => {
            if (!window.grecaptcha || !window.grecaptcha.enterprise) {
                fail('enterprise_missing');
                return;
            }
            try {
                window.grecaptcha.enterprise.ready(() => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    resolve(window.grecaptcha.enterprise);
                });
            } catch (error) {
                fail('ready_error');
            }
        };
        try {
            document.head.appendChild(script);
        } catch (error) {
            fail('script_insert_error');
        }
    });
    return loading;
};

// Call only when submitting a protected action. Never cache or persist tokens.
export default async function executeRecaptcha(action) {
    logRecaptchaStart(action);
    const enterprise = await loadEnterprise(action);
    return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        let timer;
        let settled = false;
        const fail = (reason, details) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            logRecaptchaError(action, 'execute', reason, startedAt, details);
            reject(new Error(errorMessage));
        };
        timer = setTimeout(() => fail('timeout'), 15000);
        Promise.resolve().then(() => enterprise.execute(siteKey, { action })).then((token) => {
            if (settled) return;
            clearTimeout(timer);
            if (!token) fail('empty_token');
            else {
                settled = true;
                resolve(token);
            }
        }).catch((error) => {
            fail('rejected', { errorName: safeCode(error && error.name) });
        });
    });
}

const logRequestFailure = (action, error, attempt, startedAt) => {
    const response = error && error.response;
    const data = response && response.data;
    const browserError = data && data.code === 'RECAPTCHA_BROWSER_ERROR';
    const messages = data && data.errors && Object.values(data.errors);
    const recaptchaResponse = (data && typeof data.message === 'string' && data.message.includes('reCAPTCHA'))
        || (Array.isArray(messages) && messages.some(items => Array.isArray(items)
            && items.some(message => typeof message === 'string' && message.includes('reCAPTCHA'))));
    let reason = 'request_failed';
    if (recaptchaResponse) reason = 'verification_failed';
    if (browserError) reason = 'browser_error';
    logRecaptchaError(action, 'assessment', reason, startedAt, {
        attempt,
        httpStatus: response && response.status,
        responseCode: safeCode(data && data.code),
        networkCode: safeCode(error && error.code),
        errorName: safeCode(error && error.name),
    });
    return browserError;
};

// Google may return a BROWSER_ERROR token after execute() appears to succeed.
// The API confirms that verdict; retry once with a newly generated token.
export const requestWithRecaptcha = async (action, request) => {
    const token = await executeRecaptcha(action);
    const startedAt = Date.now();
    try {
        return await request(token);
    } catch (error) {
        if (!logRequestFailure(action, error, 1, startedAt)) {
            throw error;
        }

        const retryToken = await executeRecaptcha(action);
        const retryStartedAt = Date.now();
        try {
            return await request(retryToken);
        } catch (retryError) {
            logRequestFailure(action, retryError, 2, retryStartedAt);
            throw retryError;
        }
    }
};
